/**
 * WORLD canvas-generated textures — brick, asphalt (wet), concrete, plaster,
 * wood, frosted glass, and a procedural night cube env map.
 * Zero asset files; everything painted in code.
 */
import { CanvasTexture, CubeTexture, RepeatWrapping, SRGBColorSpace } from 'three'

const BRICK_CANVAS_SIZE = 256
const ASPHALT_CANVAS_SIZE = 512
const SMALL_CANVAS_SIZE = 256
const ENV_FACE_SIZE = 64

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

const get2d = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('WORLD: 2d canvas context unavailable')
  }
  return context
}

const speckle = (
  context: CanvasRenderingContext2D,
  size: number,
  count: number,
  style: string
): void => {
  context.fillStyle = style
  for (let i = 0; i < count; i += 1) {
    context.fillRect(Math.random() * size, Math.random() * size, 1.5, 1.5)
  }
}

const makeRepeating = (canvas: HTMLCanvasElement, srgb: boolean): CanvasTexture => {
  const texture = new CanvasTexture(canvas)
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  if (srgb) {
    texture.colorSpace = SRGBColorSpace
  }
  return texture
}

// --- Brick -------------------------------------------------------------------

export const createBrickTexture = (hue: number): CanvasTexture => {
  const size = BRICK_CANVAS_SIZE
  const canvas = createCanvas(size, size)
  const context = get2d(canvas)
  context.fillStyle = '#1f1c19'
  context.fillRect(0, 0, size, size)

  const brickWidth = 32
  const brickHeight = 16
  const mortar = 2
  for (let row = 0; row * brickHeight < size; row += 1) {
    const offset = row % 2 === 0 ? 0 : brickWidth / 2
    for (let x = -brickWidth; x < size; x += brickWidth) {
      const lightness = 13 + Math.random() * 9
      const saturation = 22 + Math.random() * 12
      context.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`
      context.fillRect(
        x + offset + mortar,
        row * brickHeight + mortar,
        brickWidth - mortar * 2,
        brickHeight - mortar * 2
      )
    }
  }
  speckle(context, size, 1400, 'rgba(0, 0, 0, 0.28)')
  speckle(context, size, 350, 'rgba(255, 255, 255, 0.05)')
  return makeRepeating(canvas, true)
}

// --- Wet asphalt ----------------------------------------------------------------

export interface AsphaltMaps {
  readonly map: CanvasTexture
  readonly roughnessMap: CanvasTexture
}

export const createAsphaltMaps = (): AsphaltMaps => {
  const size = ASPHALT_CANVAS_SIZE
  const albedoCanvas = createCanvas(size, size)
  const albedo = get2d(albedoCanvas)
  albedo.fillStyle = '#0b0c10'
  albedo.fillRect(0, 0, size, size)
  speckle(albedo, size, 5000, 'rgba(200, 210, 225, 0.04)')
  speckle(albedo, size, 3000, 'rgba(0, 0, 0, 0.35)')
  // Faint dashed center line (canvas repeats along the street length).
  albedo.fillStyle = 'rgba(190, 170, 90, 0.14)'
  for (let y = 0; y < size; y += 64) {
    albedo.fillRect(size / 2 - 3, y, 6, 36)
  }

  const roughCanvas = createCanvas(size, size)
  const rough = get2d(roughCanvas)
  rough.fillStyle = '#7a7a7a'
  rough.fillRect(0, 0, size, size)
  // Puddles: smooth (dark) elliptical patches that catch the env reflection.
  const puddleCount = 26
  for (let i = 0; i < puddleCount; i += 1) {
    rough.fillStyle = `rgba(12, 12, 12, ${0.5 + Math.random() * 0.4})`
    rough.beginPath()
    rough.ellipse(
      Math.random() * size,
      Math.random() * size,
      14 + Math.random() * 46,
      8 + Math.random() * 22,
      Math.random() * Math.PI,
      0,
      Math.PI * 2
    )
    rough.fill()
  }
  speckle(rough, size, 4000, 'rgba(255, 255, 255, 0.08)')
  return {
    map: makeRepeating(albedoCanvas, true),
    roughnessMap: makeRepeating(roughCanvas, false)
  }
}

// --- Concrete (sidewalks, alley) ---------------------------------------------------

export const createConcreteTexture = (): CanvasTexture => {
  const size = SMALL_CANVAS_SIZE
  const canvas = createCanvas(size, size)
  const context = get2d(canvas)
  // Darkened ~30% (QA round 2): the clue's silhouette must pop against the
  // pavement, and mid-grey slabs read as untextured greybox.
  context.fillStyle = '#1d1e22'
  context.fillRect(0, 0, size, size)
  speckle(context, size, 2600, 'rgba(255, 255, 255, 0.05)')
  speckle(context, size, 1800, 'rgba(0, 0, 0, 0.3)')
  // Expansion joints.
  context.strokeStyle = 'rgba(0, 0, 0, 0.55)'
  context.lineWidth = 3
  context.beginPath()
  context.moveTo(0, size / 2)
  context.lineTo(size, size / 2)
  context.moveTo(size / 2, 0)
  context.lineTo(size / 2, size)
  context.stroke()
  return makeRepeating(canvas, true)
}

// --- Plaster (office walls) ----------------------------------------------------------

export const createPlasterTexture = (): CanvasTexture => {
  const size = SMALL_CANVAS_SIZE
  const canvas = createCanvas(size, size)
  const context = get2d(canvas)
  context.fillStyle = '#46413a'
  context.fillRect(0, 0, size, size)
  speckle(context, size, 2200, 'rgba(0, 0, 0, 0.18)')
  speckle(context, size, 900, 'rgba(255, 250, 230, 0.04)')
  return makeRepeating(canvas, true)
}

// --- Wood (office floor / desk) ---------------------------------------------------------

export const createWoodTexture = (): CanvasTexture => {
  const size = SMALL_CANVAS_SIZE
  const canvas = createCanvas(size, size)
  const context = get2d(canvas)
  context.fillStyle = '#2a1c11'
  context.fillRect(0, 0, size, size)
  const plankHeight = 32
  for (let y = 0; y < size; y += plankHeight) {
    context.fillStyle = `hsl(24, ${30 + Math.random() * 12}%, ${10 + Math.random() * 6}%)`
    context.fillRect(0, y, size, plankHeight - 2)
    context.strokeStyle = 'rgba(0, 0, 0, 0.4)'
    for (let g = 0; g < 6; g += 1) {
      const grainY = y + Math.random() * plankHeight
      context.beginPath()
      context.moveTo(0, grainY)
      context.bezierCurveTo(size * 0.3, grainY + 3, size * 0.7, grainY - 3, size, grainY)
      context.stroke()
    }
  }
  return makeRepeating(canvas, true)
}

// --- Frosted glass (office door pane) ---------------------------------------------------

export const createFrostedGlassTexture = (label: string): CanvasTexture => {
  const size = SMALL_CANVAS_SIZE
  const canvas = createCanvas(size, size)
  const context = get2d(canvas)
  context.fillStyle = '#c9d2d6'
  context.fillRect(0, 0, size, size)
  speckle(context, size, 6000, 'rgba(255, 255, 255, 0.25)')
  speckle(context, size, 6000, 'rgba(120, 130, 140, 0.25)')
  context.fillStyle = 'rgba(20, 22, 24, 0.8)'
  context.font = 'bold 26px Georgia, serif'
  context.textAlign = 'center'
  const lines = label.split('\n')
  lines.forEach((line, index) => {
    context.fillText(line, size / 2, size / 2 - 14 + index * 34, size - 24)
  })
  return makeRepeating(canvas, true)
}

// --- Night env map (wet-street reflections) ------------------------------------------------

const paintEnvFace = (top: string, bottom: string, smudges: number): HTMLCanvasElement => {
  const size = ENV_FACE_SIZE
  const canvas = createCanvas(size, size)
  const context = get2d(canvas)
  const gradient = context.createLinearGradient(0, 0, 0, size)
  gradient.addColorStop(0, top)
  gradient.addColorStop(1, bottom)
  context.fillStyle = gradient
  context.fillRect(0, 0, size, size)
  const neonHues = [195, 320, 45]
  for (let i = 0; i < smudges; i += 1) {
    const hue = neonHues[i % neonHues.length] ?? 195
    context.fillStyle = `hsla(${hue}, 90%, 60%, 0.16)`
    context.beginPath()
    context.ellipse(
      Math.random() * size,
      size * (0.5 + Math.random() * 0.4),
      4 + Math.random() * 10,
      2 + Math.random() * 5,
      0,
      0,
      Math.PI * 2
    )
    context.fill()
  }
  return canvas
}

export const createNightEnvMap = (): CubeTexture => {
  const sky = '#05070d'
  const horizon = '#1c2433'
  const faces = [
    paintEnvFace(sky, horizon, 5), // px
    paintEnvFace(sky, horizon, 5), // nx
    paintEnvFace(sky, sky, 0), // py
    paintEnvFace(horizon, horizon, 2), // ny
    paintEnvFace(sky, horizon, 6), // pz
    paintEnvFace(sky, horizon, 4) // nz
  ]
  const texture = new CubeTexture(faces)
  texture.colorSpace = SRGBColorSpace
  texture.needsUpdate = true
  return texture
}
